import { UserRound } from 'lucide-react';
import type { PostCreator } from '@mg/shared';
import { useAuth } from '../../auth/AuthContext';
import { useUserAvatar } from '../../auth/UserAvatarContext';
import { Avatar } from '../../components/ui/Avatar';
import { MemberAvatar } from '../../components/ui/MemberAvatar';
import { cn } from '../../lib/cn';

/** The two places a post's creator is shown: the campaign card (22 at full size) and the All Posts table (28). */
export type PostCreatorAvatarSize = 'xs22' | 'sm28';

/** The "creator unknown" circle, the same size as Avatar's preset of the same name. */
const CIRCLE: Record<PostCreatorAvatarSize, string> = {
  xs22: 'h-[1.375rem] w-[1.375rem]',
  sm28: 'h-7 w-7',
};
const GLYPH: Record<PostCreatorAvatarSize, string> = {
  xs22: 'h-3.5 w-3.5',
  sm28: 'h-4 w-4',
};

const UNKNOWN = 'Creator unknown';

export interface PostCreatorAvatarProps {
  /**
   * Who created the post. `null` = not known (a post made before creators were recorded); `undefined` =
   * not loaded. Both show the neutral "Creator unknown" circle.
   */
  person: PostCreator | null | undefined;
  size: PostCreatorAvatarSize;
  /**
   * The name is printed beside it (the campaign card), so the picture is hidden from screen readers
   * rather than read twice. Otherwise (the table) the name is its accessible label.
   */
  decorative?: boolean;
}

/**
 * A post's creator as a small round avatar: their photo, else their initials, with their name as the
 * hover tooltip (native title, the app's convention) and, unless decorative, as the accessible label.
 *
 * The viewer's own posts use the viewer's photo as the top bar shows it (an upload or the Microsoft 365
 * photo, which only the viewer can fetch); anyone else's use the member avatar proxy, which serves
 * uploads only (SourcesPage does the same). A known creator needs AuthProvider and UserAvatarProvider
 * above it (the app shell has both); a spec that renders one mocks `useAuth` and `useUserAvatar`. An
 * unknown creator needs neither.
 */
export function PostCreatorAvatar({ person, size, decorative = false }: PostCreatorAvatarProps) {
  if (!person) return <UnknownCreator size={size} decorative={decorative} />;
  return <KnownCreator person={person} size={size} decorative={decorative} />;
}

function UnknownCreator({ size, decorative }: { size: PostCreatorAvatarSize; decorative: boolean }) {
  return (
    <span
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': UNKNOWN })}
      title={UNKNOWN}
      className={cn('flex shrink-0 items-center justify-center rounded-full bg-chip text-ink-muted', CIRCLE[size])}
    >
      <UserRound aria-hidden="true" className={GLYPH[size]} />
    </span>
  );
}

function KnownCreator({
  person,
  size,
  decorative,
}: {
  person: PostCreator;
  size: PostCreatorAvatarSize;
  decorative: boolean;
}) {
  const viewerId = useAuth().principal?.user.id;
  const { photoUrl: ownPhotoUrl } = useUserAvatar();
  const own = viewerId !== undefined && viewerId === person.id;
  return (
    // One labelled image for both the photo and the initials: the picture inside is decorative.
    <span
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': person.name })}
      title={person.name}
      className="inline-flex shrink-0"
    >
      {own ? (
        <Avatar name={person.name} size={size} fetchCustom={false} photoUrl={ownPhotoUrl} decorative />
      ) : (
        <MemberAvatar person={person} size={size} decorative />
      )}
    </span>
  );
}
